export const COOKIE_CONSENT_KEY = "cookieConsent"
export const COOKIE_CONSENT_EVENT = "cookie-consent"

export type CookieConsentChoice = "all" | "necessary" | "declined"

export interface PendingGtagEvent {
  event: string
  params?: Record<string, unknown>
}

type ConsentListener = (choice: CookieConsentChoice) => void

const listeners = new Set<ConsentListener>()

let memory: CookieConsentChoice | null | undefined
let gtagQueue: PendingGtagEvent[] = []
let pixelPageViews = 0

function isChoice(value: unknown): value is CookieConsentChoice {
  return value === "all" || value === "necessary" || value === "declined"
}

export function getCookieConsent(): CookieConsentChoice | null {
  if (memory !== undefined) return memory

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    memory = isChoice(stored) ? stored : null
  } catch {
    memory = null
  }

  return memory
}

/** Accept all and Necessary only both allow analytics. Decline does not. */
export function hasTrackingConsent(): boolean {
  const choice = getCookieConsent()
  return choice === "all" || choice === "necessary"
}

export function isTrackingDeclined(): boolean {
  return getCookieConsent() === "declined"
}

export function enqueueGtagEvent(event: string, params?: Record<string, unknown>): void {
  gtagQueue.push({ event, params })
}

export function drainGtagQueue(): PendingGtagEvent[] {
  const queued = gtagQueue
  gtagQueue = []
  return queued
}

export function enqueuePixelPageView(): void {
  pixelPageViews += 1
}

export function drainPixelPageViews(): number {
  const queued = pixelPageViews
  pixelPageViews = 0
  return queued
}

export function clearTrackingQueues(): void {
  gtagQueue = []
  pixelPageViews = 0
}

/** Test isolation. The in-memory choice survives a localStorage wipe. */
export function resetCookieConsentState(): void {
  memory = undefined
  clearTrackingQueues()
}

/** Fired after a choice is stored, including a replay-free late subscription. */
export function subscribeCookieConsent(listener: ConsentListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function setCookieConsent(choice: CookieConsentChoice): void {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, choice)
  } catch {
    // Preference still applies for this page load.
  }

  memory = choice

  if (choice === "declined") clearTrackingQueues()

  for (const listener of listeners) {
    try {
      listener(choice)
    } catch {
      /* a listener must not block the choice */
    }
  }

  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: choice }))
}
